import { SpecialNotes } from "@fokused-me/core";
import {
   FMAppBar,
   FMAppContainer,
   FMAppMenuPanel,
   FMConfirmDialog,
   FMPolicyDialog,
   FMSettingsDialog,
   ItemPreview,
   MenuCommand,
   NodeTargetZone,
   NotepadType,
   SelectionMenuCommand,
} from "@fokused-me/web-ui";
import { formatRFC7231, parseISO } from "date-fns";
import { List } from "immutable";
import { Fragment, useCallback, useMemo, useState } from "react";
import { DragDropContext } from "react-dnd";
import { createPanelInfo, useDesktop } from "../../state/Desktop";
import { useNotepads } from "../../state/Notepads";
import { usePolicy } from "../../state/Policy";
import { useSession } from "../../state/Session";
import { HELP_URL } from "../../utils/consts";
import { appSystemInfo } from "../../utils/environment";
import { ManageNotepadPage } from "../ManageNotepadPage";
import { ContentPanelSelector } from "./content/ContentPanelSelector";
import { processNoteTreeNodeMoveEvent, useDeleteNoteWithConfirmation } from "./content/Helpers";

const Component = (props) => {
   const { notepadInfos, notepads, onlineStatus } = useNotepads();
   const { panels, addPanel, desktopInfo, toggleSelectionCopyOnDrop, toggleSelectionClearOnDrop, clearNoteSelection } =
      useDesktop();
   const { endSession } = useSession();
   const { result: policy } = usePolicy();

   const [showManageNotepads, setShowManageNotepads] = useState(false);
   const [showLockConfirmation, setShowLockConfirmation] = useState(false);
   const [showSettingsDialog, setShowSettingsDialog] = useState(false);
   const [showPolicyDialog, setShowPolicyDialog] = useState(false);

   const handleCloseManageNotepads = useCallback(() => setShowManageNotepads(false), [setShowManageNotepads]);

   const handleMenuCommand = useCallback(
      (command: MenuCommand) => {
         switch (command.type) {
            case "OPEN_NOTEPAD":
               const panel = createPanelInfo({
                  notepadId: command.notepadId,
                  rootNoteId: SpecialNotes.HOME,
                  type: "tree",
               });
               addPanel(panel);
               break;
            case "MANAGE_NOTEPADS":
               setShowManageNotepads(true);
               break;
            case "SETTINGS":
               setShowSettingsDialog(true);
               break;
            case "POLICY":
               setShowPolicyDialog(true);
               break;
            case "HELP":
               window.open(HELP_URL, "_blank");
               break;
         }
      },
      [setShowManageNotepads, addPanel, setShowPolicyDialog, setShowSettingsDialog]
   );

   const { confirmDeleteDialog, handleDeleteNotes } = useDeleteNoteWithConfirmation(notepads);

   const handleSelectionMenuCommand = useCallback(
      (command: SelectionMenuCommand) => {
         switch (command.type) {
            case "CLEAR_SELECTION":
               clearNoteSelection();
               break;
            case "TOGGLE_COPY_NO_DROP":
               toggleSelectionCopyOnDrop();
               break;
            case "TOGGLE_CLEAR_ON_DROP":
               toggleSelectionClearOnDrop();
               break;
            case "MOVE_SELECTION_TO_BIN":
               handleDeleteNotes(desktopInfo.selectedNotes);
               break;
         }
      },
      [desktopInfo, toggleSelectionCopyOnDrop, toggleSelectionClearOnDrop, clearNoteSelection, handleDeleteNotes]
   );

   const handleLockCommand = useCallback(() => {
      setShowLockConfirmation(true);
   }, []);

   const handleLockConfirmResult = useCallback(
      (result: number) => {
         if (result === 0) {
            endSession();
         }
         setShowLockConfirmation(false);
      },
      [endSession]
   );

   const handleAcceptPolicy = useCallback(() => setShowPolicyDialog(false), [setShowPolicyDialog]);

   const handleSettingsResult = useCallback(() => setShowSettingsDialog(false), [setShowSettingsDialog]);

   const menuNotepads = useMemo(
      () =>
         notepadInfos
            .filter((it) => !it.closed)
            .map((it) => ({
               id: it.id,
               name: it.name,
               type: it.type === "cloud" ? NotepadType.CLOUD : NotepadType.LOCAL,
            })),
      [notepadInfos]
   );

   const handleSelectedMove = useCallback(
      (destinationNotepadId: string, destinationId: string, zone: NodeTargetZone) => {
         processNoteTreeNodeMoveEvent(
            destinationNotepadId,
            notepads,
            {
               anchorItemId: destinationId,
               anchorType: zone,
               items: desktopInfo.selectedNotes,
            },
            desktopInfo.copyOnDrop
         );

         if (desktopInfo.clearOnDrop) {
            clearNoteSelection();
         }
      },
      [notepads, desktopInfo.selectedNotes, desktopInfo.clearOnDrop, desktopInfo.copyOnDrop, clearNoteSelection]
   );

   return (
      <Fragment>
         <FMAppBar
            notepads={menuNotepads}
            selectedNotes={desktopInfo.selectedNotes.size}
            clearOnDrop={desktopInfo.clearOnDrop}
            copyOnDrop={desktopInfo.copyOnDrop}
            onlineStatus={onlineStatus === undefined ? "NONE" : onlineStatus === "ONLINE" ? "ONLINE" : "OFFLINE"}
            onLockData={handleLockCommand}
            onMenuCommand={handleMenuCommand}
            onSelectedMove={handleSelectedMove}
            onSelectionMenuCommand={handleSelectionMenuCommand}
         />
         <FMAppContainer>
            {panels && panels.size ? (
               panels.map((panelInfo) => {
                  return <ContentPanelSelector panelInfo={panelInfo} key={panelInfo.id} />;
               })
            ) : (
               <FMAppMenuPanel notepads={menuNotepads} onMenuCommand={handleMenuCommand} />
            )}
         </FMAppContainer>

         <ItemPreview key="__preview" name="Item" {...props} />
         {showManageNotepads ? <ManageNotepadPage initMode={false} onClose={handleCloseManageNotepads} /> : null}
         <FMConfirmDialog
            open={showLockConfirmation}
            title="Lock the app"
            text="Do you want to lock the app? You will have to enter password to use it next time."
            buttons={List.of("Lock", "Cancel")}
            onResult={handleLockConfirmResult}
         />
         {showPolicyDialog ? <FMPolicyDialog content={policy} onAccepted={handleAcceptPolicy} /> : null}
         {showSettingsDialog ? (
            <FMSettingsDialog
               about={{
                  version: appSystemInfo.version,
                  commitId: appSystemInfo.repoCommitId,
                  buildDate: formatRFC7231(parseISO(appSystemInfo.buildDate)),
               }}
               onResult={handleSettingsResult}
            />
         ) : null}
         {confirmDeleteDialog}
      </Fragment>
   );
};

const isTouchDevice = !!("ontouchstart" in window || navigator.maxTouchPoints);

/* eslint-disable @typescript-eslint/no-var-requires */
export const MainPage = DragDropContext(
   require(isTouchDevice ? "react-dnd-touch-backend" : "react-dnd-html5-backend").default
)((props) => {
   return <Component {...props} />;
});
